import type { Zone } from '@seek/audience-zones';
import type { Brand, Country, Language } from '@seek/melways-sites';
import { adapter } from '@seek/tealium-adapter';

import { useUserProfile } from 'src/components/Notifications/hooks/useUserProfile';

import { createAccountAnalytics } from './accountAnalytics';
import {
  type ProfileTrackingState,
  createProfileAnalytics,
  profileTrackingState,
  useSetProfileTrackingState,
} from './profileAnalytics';

export type AnalyticsCurrentPage =
  | 'account settings'
  | 'visibility settings'
  | 'notification settings';

interface TrackPageLoadProps {
  currentPage: AnalyticsCurrentPage;
}
export interface InitialAppState {
  brand?: Brand;
  siteCountry?: Country;
  zone?: Zone;
  siteLanguage?: Language;
  userAgent?: string;
  siteSection?: 'myaccount';
  actionOrigin?: 'settings';
}

export interface CandidateState {
  isLoggedIn: boolean;
  loginId: number;
  profileGuid: string;
}

export type TrackingState = CandidateState & ProfileTrackingState;

const tealiumAdapter = adapter();

let candidateState: CandidateState;

export const setCandidateState = (
  loginId: number,
  profileGuid: string,
): void => {
  candidateState = {
    loginId,
    profileGuid,
    isLoggedIn: Boolean(loginId),
  };
};

export const useSetCandidateState = () => {
  const { user } = useUserProfile();
  if (user) {
    setCandidateState(user.id, user.emailCorrelationId);
  }
};

export const useSetTrackingState = () => {
  useSetCandidateState();
  useSetProfileTrackingState();
};

export const allTrackingState = (): TrackingState => ({
  ...candidateState,
  ...profileTrackingState,
});

export const createAnalytics = (initialState: InitialAppState) => {
  const trackPageLoad = (props: TrackPageLoadProps) => {
    const { currentPage } = props;

    tealiumAdapter.view('settings_displayed', {
      ...initialState,
      ...candidateState,

      eventName: 'settings_displayed',
      currentPage,
    });
  };

  const accountAnalytics = createAccountAnalytics(initialState);
  const profileAnalytics = createProfileAnalytics(initialState);

  return { trackPageLoad, ...accountAnalytics, ...profileAnalytics };
};

export type Analytics = ReturnType<typeof createAnalytics>;
